import { useState, useMemo } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent, cn } from "@tellescope/ui";
import { splitEmailContent, formatDateTime, formatName, sanitizeEmailHTMLContent } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import type { ThreadMessage } from "@tellescope/tanstack-query";
import { Clock, Download, FileText } from "lucide-react";
import { useEmailContent } from "../hooks/useEmailContent";

interface EmailMessageProps {
  message: ThreadMessage;
  showPreviousChain?: boolean;
  renderFullContent?: boolean;
  className?: string;
}

export const EmailMessage = ({
  message,
  showPreviousChain = false,
  renderFullContent = false,
  className,
}: EmailMessageProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation("inbox");

  const { sanitizedLatest, sanitizedPrevious, hasPreviousChain } = useMemo(() => {
    const { latestContent, previousChain } = splitEmailContent(message.content);
    if (renderFullContent) {
      return {
        sanitizedLatest: sanitizeEmailHTMLContent(`${latestContent}${previousChain}`),
        sanitizedPrevious: "",
        hasPreviousChain: false,
      };
    }
    return {
      sanitizedLatest: sanitizeEmailHTMLContent(latestContent),
      sanitizedPrevious: sanitizeEmailHTMLContent(previousChain),
      hasPreviousChain: previousChain.trim().length > 0,
    };
  }, [message.content, renderFullContent]);

  // Process sanitized HTML with attachments
  const { processedHtml: latestProcessed, unmatchedAttachments } = useEmailContent({
    html: sanitizedLatest,
    attachments: message.attachments,
  });

  const { processedHtml: previousProcessed } = useEmailContent({
    html: sanitizedPrevious,
    attachments: showPreviousChain ? message.attachments : undefined,
  });

  const senderDisplay = useMemo(() => {
    if (!message.sender) return t("Unknown");

    const email = "email" in message.sender ? message.sender.email : undefined;
    const name = formatName(message.sender);

    return email || name || t("Unknown");
  }, [message.sender, t]);

  const timestampDisplay = useMemo(() => {
    const ts = message.isScheduled && message.sendAt ? message.sendAt : message.timestamp;
    return formatDateTime(ts, "MM/DD/YYYY at hh:mm a", "always");
  }, [message.isScheduled, message.sendAt, message.timestamp]);

  const ccDisplay = useMemo(() => {
    if (!message.cc || message.cc.length === 0) return undefined;
    return message.cc.join(", ");
  }, [message.cc]);

  // Helper to check if attachment is an image
  const isImageAttachment = (filename: string) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
    const lowerName = filename.toLowerCase();
    return imageExtensions.some((ext) => lowerName.endsWith(ext));
  };

  return (
    <div className={cn("border-t border-t-gray-200 pt-4", className)}>
      <div className="mb-3 space-y-1">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{senderDisplay}</span>
          <div className="flex items-center gap-1.5">
            {message.isScheduled && (message.sendAt?.getTime() ?? 0) >= new Date().getTime() && (
              <Clock className="h-3 w-3" />
            )}
            <span>{timestampDisplay}</span>
          </div>
        </div>
        {ccDisplay && <div className="text-xs text-muted-foreground">{t("cc: {{cc}}", { cc: ccDisplay })}</div>}
      </div>

      <div className="prose prose-sm max-w-none pb-4" dangerouslySetInnerHTML={{ __html: latestProcessed }} />

      {unmatchedAttachments.length > 0 && (
        <div className="my-4 space-y-2">
          <div className="text-xs font-medium text-muted-foreground">{t("Attachments")}</div>
          <div className="flex gap-2 flex-wrap">
            {unmatchedAttachments.map((attachment) => {
              const isImage = attachment.name && isImageAttachment(attachment.name);

              return (
                <a
                  key={attachment.secureName}
                  href={attachment.downloadUrl}
                  download={attachment.name}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors",
                    !attachment.downloadUrl && "opacity-50 cursor-not-allowed pointer-events-none"
                  )}
                  title={attachment.downloadUrl ? t("Download") : t("Loading...")}
                >
                  {isImage && attachment.downloadUrl ? (
                    <img
                      src={attachment.downloadUrl}
                      alt={attachment.name || t("Unnamed attachment")}
                      className="w-10 h-10 object-cover rounded flex-shrink-0"
                    />
                  ) : (
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className="flex-1 text-sm truncate">{attachment.name || t("Unnamed attachment")}</span>
                  {attachment.downloadUrl && <Download className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {hasPreviousChain && showPreviousChain && (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="my-2 ml-2">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <span className="text-[8px]">{isExpanded ? "▼" : "►"}</span>
            <span className="text-xs">{isExpanded ? t("Hide previous replies") : t("Show previous replies")}</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div
              className="prose prose-sm max-w-none opacity-70 pl-4 border-l-2 border-gray-200"
              dangerouslySetInnerHTML={{ __html: previousProcessed }}
            />
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
